import { ScheduleTicker } from "../app/schedule-ticker";
import { Controler } from "./controler/controler";
import { Lane } from "./lane";

export class Track {
  private lanes = new Set<Lane>();
  private listeners = new Set<() => void>();
  private unsubscribers: (() => void)[] = [];
  /** L'écoute posée sur chaque lane, rendue quand la lane part. */
  private laneUnsubscribers = new Map<Lane, () => void>();

  private muted = false;
  private hidden = false;

  /**
   * Lanes that were individually muted/hidden before a track-level mute/hide.
   * Used to restore correct per-lane state when the track is unmuted/shown.
   */
  private lanesMutedBeforeTrackMute = new Set<Lane>();
  private lanesHiddenBeforeTrackHide = new Set<Lane>();

  constructor(private controler: Controler, private ticker: ScheduleTicker) {
    this.unsubscribers.push(
      controler.on('lane:created', (lane) => this.addLane(lane)),
      controler.on('lane:removed', (lane) => this.removeLane(lane))
    );

    controler.start(this.ticker);
  }

  private addLane(lane: Lane): void {
    this.lanes.add(lane);

    // Propagate track-level state to newly created lanes
    if (this.muted && !lane.isMuted()) {
      lane.mute();
    }
    if (this.hidden && !lane.isHidden()) {
      lane.hide();
    }

    if (lane.isMuted() && this.lanes.size === 1) {
      this.mute();
    }

    // Mono-lane track: the lane *is* the track, so its mute state is the
    // track's. `size === 0` was unreachable from a lane listener — the lane
    // that fires has just been added — which left such a track logically
    // muted forever once muted, and then force-muted any lane added later.
    const unsubscribe = lane.on((e) => {
      if (e.type === 'mute') {
        if (this.lanes.size === 1) {
          this.mute();
        }
      } else if (e.type === 'unmute') {
        if (this.lanes.size === 1) {
          this.unmute();
        }
      }
    });

    // Gardé pour le rendre au départ de la lane : jeté, il restait accroché à
    // une lane que plus personne ne tenait.
    this.laneUnsubscribers.get(lane)?.();
    this.laneUnsubscribers.set(lane, unsubscribe);

    this.notifyChange();
  }

  private removeLane(lane: Lane): void {
    this.laneUnsubscribers.get(lane)?.();
    this.laneUnsubscribers.delete(lane);
    this.lanes.delete(lane);
    this.lanesMutedBeforeTrackMute.delete(lane);
    this.lanesHiddenBeforeTrackHide.delete(lane);
    this.notifyChange();
  }

  mute(): void {
    if (this.muted) return;

    this.muted = true;

    // Snapshot which lanes are already individually muted
    this.lanesMutedBeforeTrackMute.clear();
    for (const lane of this.lanes) {
      if (lane.isMuted()) {
        this.lanesMutedBeforeTrackMute.add(lane);
      } else {
        lane.mute();
      }
    }

    this.notifyChange();
  }

  unmute(): void {
    if (!this.muted) return;
    this.muted = false;

    // Restore: unmute only lanes that were NOT individually muted before
    for (const lane of this.lanes) {
      if (this.lanes.size ===  1 || !this.lanesMutedBeforeTrackMute.has(lane)) {
        lane.unmute();
      }
    }
    this.lanesMutedBeforeTrackMute.clear();

    this.notifyChange();
  }

  isMuted(): boolean {
    return this.muted;
  }

  hide(): void {
    if (this.hidden) return;
    this.hidden = true;

    // Snapshot which lanes are already individually hidden
    this.lanesHiddenBeforeTrackHide.clear();
    for (const lane of this.lanes) {
      if (lane.isHidden()) {
        this.lanesHiddenBeforeTrackHide.add(lane);
      } else {
        lane.hide();
      }
    }

    this.notifyChange();
  }

  show(): void {
    if (!this.hidden) return;
    this.hidden = false;

    // Restore: show only lanes that were NOT individually hidden before
    for (const lane of this.lanes) {
      if (!this.lanesHiddenBeforeTrackHide.has(lane)) {
        lane.show();
      }
    }
    this.lanesHiddenBeforeTrackHide.clear();

    this.notifyChange();
  }

  isHidden(): boolean {
    return this.hidden;
  }

  getControler(): Controler {
    return this.controler;
  }

  getLanes(): Lane[] {
    return Array.from(this.lanes);
  }

  onChange(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyChange() {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Retire la piste sans rien détruire de ce qu'elle présentait.
   *
   * `destroy` emporte les lanes et le contrôleur : c'est le geste de qui
   * referme un morceau. Celui-ci est le geste de qui le met **de côté** — un
   * morceau d'une séquence qui n'est plus celui sous la tête de lecture garde
   * ses notes et son synthé chaud, et remontera dans une piste neuve quand la
   * tête y reviendra (`SongSequence`). Ce qui part, c'est l'écoute posée sur le
   * contrôleur et sur chaque lane : laissée en place, elle rattacherait à une
   * piste que plus personne ne tient tout ce que ces lanes émettraient encore.
   */
  release(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
    this.laneUnsubscribers.forEach(unsub => unsub());
    this.laneUnsubscribers.clear();
    this.lanes.clear();
    this.listeners.clear();
    this.lanesMutedBeforeTrackMute.clear();
    this.lanesHiddenBeforeTrackHide.clear();
  }

  destroy(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.laneUnsubscribers.forEach(unsub => unsub());
    this.laneUnsubscribers.clear();
    this.lanes.forEach(lane => lane.destroy());
    this.lanes.clear();
    this.controler.destroy?.();
    this.listeners.clear();
    this.lanesMutedBeforeTrackMute.clear();
    this.lanesHiddenBeforeTrackHide.clear();
  }
}
